import jobBoardApi from '../../api/job-board';
import { clearStoredCriteria, readStoredCriteria } from './useGuestCriteria';
import { fromCriteriaPayload, toCriteriaPayload } from './useCriteria';

/**
 * The signed-in half of the Build-Your-Own-Algorithm surface, and the handoff between the two
 * halves (EN-859).
 *
 * A visitor edits their algorithm against localStorage because they have no `job_board_settings`
 * row to save it into. Registering gives them one, so the set they built while signed out is
 * carried across on their first authenticated visit to the board and the local copy is dropped.
 *
 * Both endpoints sit behind `features:criteria-weighted-search`, which 400s rather than falling
 * through — callers must gate on the flag before calling either.
 *
 * @see docs/architecture/job-board/criteria-contract.md
 */

/**
 * The seeker's saved algorithm, or the seeded default when they have never saved one.
 *
 * A failure resolves to null rather than rejecting, matching `fetchGuestCriteria()`: the panel
 * is an enhancement, and a seeker whose request failed should get the board with the default
 * order rather than a broken one.
 *
 * @returns {Promise<Array<{key: string, userBucket: string}>|null>}
 */
export async function fetchSavedCriteria() {
	return (await fetchSavedSettings())?.criteria ?? null;
}

/**
 * The saved criteria plus the other Job Board Settings stored on the same row — today only the
 * EN-1066 Complete Auto Apply opt-in. Null on failure, like `fetchSavedCriteria()`.
 *
 * @returns {Promise<{criteria: Array<{key: string, userBucket: string}>|null, completeAutoApply: boolean}|null>}
 */
export async function fetchSavedSettings() {
	try {
		const data = await jobBoardApi.getSavedCriteria();

		return {
			criteria: data?.criteria ? fromCriteriaPayload(data) : null,
			completeAutoApply: data?.complete_auto_apply === true,
		};
	} catch {
		return null;
	}
}

/**
 * Write a set into the seeker's saved settings and hydrate the stored answer.
 *
 * Serialized against an empty filter state on purpose: the panel edits priority and hard/soft
 * status, and `value` comes from the live filter bar on every search. Persisting the values the
 * seeker happened to have typed at save time would bake one session's filters into their
 * standing algorithm.
 *
 * Unlike the two fetches above this **rejects** on failure — a save is the one criteria call
 * whose outcome the caller has to act on, and the store turns it into a notification.
 *
 * @returns {Promise<Array<{key: string, userBucket: string}>>} the stored set, renumbered to a
 *   contiguous ranked stack by the server
 */
export async function persistCriteria(criteria, { completeAutoApply } = {}) {
	const payload = toCriteriaPayload(criteria, {});

	if (typeof completeAutoApply === 'boolean') {
		payload.complete_auto_apply = completeAutoApply;
	}

	const data = await jobBoardApi.saveCriteria(payload);

	return data?.criteria ? fromCriteriaPayload(data) : criteria;
}

/**
 * Carry the set this browser built while signed out into the seeker's saved settings.
 *
 * The local copy is dropped only once the write has landed, so a failed carry-over leaves the
 * set where a later visit can retry it.
 *
 * @returns {Promise<Array<{key: string, userBucket: string}>|null>} the adopted set, or null
 *   when this browser has nothing stored or the write failed
 */
export async function adoptGuestCriteria() {
	const stored = readStoredCriteria();

	if (stored === null) {
		return null;
	}

	try {
		const adopted = await persistCriteria(stored);

		clearStoredCriteria();

		return adopted;
	} catch {
		return null;
	}
}
